import React, { useEffect } from 'react';
import { ShieldCheck, AlertTriangle, XCircle, CheckCircle2, FileText, FolderOpen, ListChecks, MessageSquare, Download, LayoutList, Send, ChevronDown, Lock } from 'lucide-react';
import { CountdownTimer } from './components/CountdownTimer';
import { LegalFooter } from './components/LegalFooter';
import { initMetaPixel, trackPageView, trackEvent } from './lib/metaPixel';
function smoothScrollTo(targetId: string, duration = 1600) {
  const target = document.getElementById(targetId);
  if (!target) return;
  const startY = window.scrollY;
  const endY = target.getBoundingClientRect().top + startY;
  const distance = endY - startY;
  const startTime = performance.now();
  const easeInOutCubic = (t: number) => t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
  const step = (now: number) => {
    const elapsed = now - startTime;
    const progress = Math.min(elapsed / duration, 1);
    window.scrollTo(0, startY + distance * easeInOutCubic(progress));
    if (progress < 1) requestAnimationFrame(step);
  };
  requestAnimationFrame(step);
}
export function App() {
  useEffect(() => {
    // Initialize the Meta Pixel and fire PageView when the page loads.
    initMetaPixel();
    trackPageView();
  }, []);
  const handleCheckoutClick = () => {
    // Fire AddToCart right before redirecting to the Shopify cart.
    trackEvent('AddToCart', {
      content_name: 'Visa-Prep: La Guía Universal de Inmigración',
      content_ids: ['51890895323457'],
      content_type: 'product',
      value: 27.0,
      currency: 'USD'
    });
  };
  return <div className="min-h-screen bg-slate-50 font-sans text-slate-800 selection:bg-yellow-200" data-id="element-0" data-mp="38m1jadapbvsn">
      {/* URGENCY BAR */}
      <div className="bg-yellow-500 text-slate-900 text-center text-sm md:text-base font-semibold py-2.5 px-4" data-id="element-1" data-mp="3dlw6a5rcw18f">
        ⏳ Oferta especial por solo 24 horas — Precio de lanzamiento $27 USD (después $57)
      </div>

      {/* HEADER / HERO SECTION */}
      <header className="bg-slate-900 text-white pt-16 pb-20 px-4 relative overflow-hidden" data-id="element-2" data-mp="swgmhuqk1csa">
        <div className="absolute inset-0 opacity-10 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')]" data-id="element-3" data-mp="38ktgau95lfg"></div>
        <div className="max-w-5xl mx-auto relative z-10" data-id="element-4" data-mp="d4e57s05daw6">
          <div className="text-center mb-10" data-id="element-5" data-mp="d3wax0x1p6qw">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-yellow-500/20 text-yellow-400 font-semibold text-sm mb-6 border border-yellow-500/30" data-id="element-6" data-mp="851lvh8hilyc">
              <ShieldCheck size={18} data-id="element-7" data-mp="3gj1vqgs7dd7e" />
              <span data-id="element-8" data-mp="3bn314bga6yyj">Sistema de Expediente Blindado</span>
            </div>
            <h1 className="text-4xl md:text-6xl font-extrabold leading-tight mb-6 tracking-tight" data-id="element-9" data-mp="3qaxsnoijyxa1">
              Visa-Prep: El Sistema de Expediente Blindado para tu{' '}
              <span className="text-yellow-400" data-id="element-10" data-mp="3lief2rjv8258">Ajuste de Estatus en USA</span>
            </h1>
            <h2 className="text-xl md:text-2xl text-slate-300 max-w-3xl mx-auto leading-relaxed font-light" data-id="element-11" data-mp="2xssh27f8bsyt">
              Envía tu solicitud de residencia por matrimonio o familia con la
              seguridad de un experto, sin gastar miles de dólares en abogados y
              evitando los errores que causan rechazos inmediatos.
            </h2>
          </div>

          <div className="flex flex-col md:flex-row items-center gap-12 mt-12" data-id="element-12" data-mp="ygwkvd1b1ezj">
            <div className="w-full md:w-1/2 flex justify-center" data-id="element-13" data-mp="13dwtnvpjgvuc">
              <div className="relative" data-id="element-14" data-mp="1r4987pfahqtw">
                <div className="absolute -inset-4 bg-yellow-500/20 blur-xl rounded-full" data-id="element-15" data-mp="1r5ams3vikvgd"></div>
                <img src="https://cdn.magicpatterns.com/uploads/qUb4Yp8e1qcjSx6WBDA6Mc/VISA_1.4.png" alt="Visa-Prep Mockup" className="relative z-10 w-full max-w-md rounded-xl shadow-2xl border border-slate-700" data-id="element-16" data-mp="1w2aqw98szo4u" />
              </div>
            </div>
            <div className="w-full md:w-1/2 space-y-6" data-id="element-17" data-mp="6mujayaj2ah2">
              <div className="bg-slate-800/50 p-6 rounded-2xl border border-slate-700" data-id="element-18" data-mp="aqiqvgz5t62b">
                <h3 className="text-xl font-bold text-white mb-4" data-id="element-19" data-mp="qwvos6dw892o">
                  Lo que conseguirás hoy:
                </h3>
                <ul className="space-y-3" data-id="element-20" data-mp="m5e0aht240yl">
                  {['Guía Maestra paso a paso (I-485, I-130, etc.)', 'Método de Etiquetado Color-Code', 'Matriz de Evidencia Cruzada', "Checklist de 'Cero Errores'", 'Guía de Preparación para la Entrevista'].map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-21" data-mp="14wnayuekcvrr" data-mpi={i}>
                      <CheckCircle2 className="text-yellow-400 shrink-0 mt-0.5" size={20} data-id="element-22" data-mp="zxtuct3zcond" />
                      <span className="text-slate-300" data-id="element-23" data-mp="vvp7cd81eq9p">{item}</span>
                    </li>)}
                </ul>
                <div className="mt-8" data-id="element-24" data-mp="3gmh6gfbk5g9c">
                  <a href="#checkout" onClick={e => {
                  e.preventDefault();
                  smoothScrollTo('checkout', 1600);
                }} className="block w-full bg-yellow-500 hover:bg-yellow-400 text-slate-900 text-center font-bold text-lg py-4 px-8 rounded-xl shadow-[0_0_20px_rgba(234,179,8,0.4)] transition-all transform hover:scale-[1.02]" data-id="element-25" data-mp="3gleh4btvvlvj">
                    QUIERO MI GUÍA VISA-PREP AHORA
                  </a>
                  <p className="text-center text-slate-400 text-sm mt-3 flex items-center justify-center gap-1" data-id="element-26" data-mp="2eumxgg37err4">
                    <Lock size={14} data-id="element-27" data-mp="2jvuaouo48j56" /> Pago 100% seguro y encriptado
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* OPENING HOOK */}
      <section className="py-20 px-4 bg-white" data-id="element-28" data-mp="thtobow3tx9i">
        <div className="max-w-3xl mx-auto text-center space-y-8" data-id="element-29" data-mp="tj4shptgp715">
          <AlertTriangle className="mx-auto text-red-500" size={48} data-id="element-30" data-mp="yg51a8hp4nvy" />
          <p className="text-2xl md:text-3xl font-medium text-slate-900 leading-relaxed" data-id="element-31" data-mp="21rkg7pci469n">
            La mayoría de los inmigrantes en Estados Unidos viven con un miedo
            constante: que el sobre que enviaron a USCIS regrese con una
            notificación de rechazo o, peor aún, una orden de deportación por un
            error técnico que pudieron evitar.
          </p>
          <p className="text-lg text-slate-600 leading-relaxed" data-id="element-32" data-mp="25va4edp9klu5">
            No es la falta de documentos lo que detiene los casos; es la falta
            de claridad. Un error de dedo, una fecha contradictoria o una firma
            fuera de lugar son suficientes para que un oficial descarte años de
            sueños en segundos.
          </p>
        </div>
      </section>

      {/* THE PROBLEM */}
      <section className="py-20 px-4 bg-slate-100 border-y border-slate-200" data-id="element-33" data-mp="2pjit2drlm9ow">
        <div className="max-w-4xl mx-auto" data-id="element-34" data-mp="33h7f3oxejmvj">
          <div className="text-center mb-12" data-id="element-35" data-mp="2yi3731m7rv6s">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" data-id="element-36" data-mp="2yjec14reb8zh">
              Llenar los formularios es la parte fácil.
            </h2>
            <p className="text-xl text-slate-600" data-id="element-37" data-mp="3n2g39yxmtpxz">
              Lo difícil es construir un expediente que no deje espacio a la
              duda.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8" data-id="element-38" data-mp="3d4jql2p1qmxr">
            <div className="bg-white p-8 rounded-2xl shadow-sm border border-slate-200" data-id="element-39" data-mp="3d38kuxc1j540">
              <div className="w-12 h-12 bg-red-100 text-red-600 rounded-xl flex items-center justify-center mb-6" data-id="element-40" data-mp="2zc3teiittj4r">
                <span className="text-2xl font-bold" data-id="element-41" data-mp="345er9u5nupkj">$</span>
              </div>
              <h3 className="text-xl font-bold mb-3" data-id="element-42" data-mp="2pg0h4ju9g443">Honorarios Imposibles</h3>
              <p className="text-slate-600" data-id="element-43" data-mp="2udiv4on4x9iy">
                Honorarios legales que oscilan entre los $3,000 y $7,000
                dólares, dinero que preferirías usar para tu nuevo hogar.
              </p>
            </div>
            <div className="bg-white p-8 rounded-2xl shadow-sm border border-slate-200" data-id="element-44" data-mp="2kekxmw7qe5nt">
              <div className="w-12 h-12 bg-orange-100 text-orange-600 rounded-xl flex items-center justify-center mb-6" data-id="element-45" data-mp="30uc9s7a8ugnx">
                <MessageSquare size={24} data-id="element-46" data-mp="2vwtw63la7d9l" />
              </div>
              <h3 className="text-xl font-bold mb-3" data-id="element-47" data-mp="2qwel5ioh2hus">Desinformación</h3>
              <p className="text-slate-600" data-id="element-48" data-mp="2ly4qd96ntj9p">
                Información contradictoria en grupos de Facebook y YouTube que
                solo aumenta tu ansiedad y confusión.
              </p>
            </div>
            <div className="bg-white p-8 rounded-2xl shadow-sm border border-slate-200" data-id="element-49" data-mp="2d62qbc0i64zy">
              <div className="w-12 h-12 bg-yellow-100 text-yellow-600 rounded-xl flex items-center justify-center mb-6" data-id="element-50" data-mp="286pwg7b7rxnv">
                <FileText size={24} data-id="element-51" data-mp="238ej3mlpn50w" />
              </div>
              <h3 className="text-xl font-bold mb-3" data-id="element-52" data-mp="235ioo892myh7">El Miedo al RFE</h3>
              <p className="text-slate-600" data-id="element-53" data-mp="1y80md0pk7hi6">
                El miedo paralizante de recibir una RFE (Solicitud de Evidencia)
                que retrase tu permiso de trabajo por meses o años.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* WHY WHAT YOU TRIED DIDN'T WORK */}
      <section className="py-20 px-4 bg-white" data-id="element-54" data-mp="dskot2ytdibh">
        <div className="max-w-4xl mx-auto" data-id="element-55" data-mp="ipb25ij8hzt7">
          <h2 className="text-3xl font-bold text-center mb-12" data-id="element-56" data-mp="iqdvry7xygct">
            Por qué lo que has intentado no funciona
          </h2>
          <div className="space-y-6" data-id="element-57" data-mp="3m6kvxur5dvgu">
            {['Los tutoriales de YouTube suelen ser casos específicos que no aplican a tu situación personal o legal.', "Los consejos de 'amigos' se basan en leyes que cambiaron hace meses; USCIS actualiza sus criterios constantemente.", 'Intentar adivinar lo que significa el lenguaje legal de los formularios es la receta perfecta para el desastre administrativo.'].map((text, i) => <div key={i} className="flex items-start gap-4 p-6 bg-red-50 rounded-xl border border-red-100" data-id="element-58" data-mp="33dz21fzsweyw" data-mpi={i}>
                <XCircle className="text-red-500 shrink-0 mt-1" size={24} data-id="element-59" data-mp="2f6byln1aatq7" />
                <p className="text-lg text-slate-800" data-id="element-60" data-mp="2k5f5ld0tra0t">{text}</p>
              </div>)}
          </div>
        </div>
      </section>

      {/* THE UNIQUE MECHANISM */}
      <section className="py-24 px-4 bg-slate-900 text-white relative overflow-hidden" data-id="element-61" data-mp="2u44bs8jcz618">
        <div className="absolute top-0 right-0 w-96 h-96 bg-yellow-500/10 rounded-full blur-3xl" data-id="element-62" data-mp="1wgnih023dhuw"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl" data-id="element-63" data-mp="21fqe5tw6mbq7"></div>

        <div className="max-w-4xl mx-auto text-center relative z-10" data-id="element-64" data-mp="26etgh6ks23uh">
          <h2 className="text-4xl font-bold mb-6" data-id="element-65" data-mp="26cqjg2l7jag9">
            Presentamos el{' '}
            <span className="text-yellow-400" data-id="element-66" data-mp="2b7342fyf6rtl">
              Sistema de Expediente Blindado
            </span>
          </h2>
          <p className="text-xl text-slate-300 leading-relaxed mb-10" data-id="element-67" data-mp="1dobhohr36omv">
            Este no es un simple eBook con instrucciones; es un método de
            ingeniería documental. El sistema te enseña a pensar como el oficial
            de USCIS que revisará tu caso, permitiéndote organizar tu evidencia
            de forma que su única opción lógica sea aprobarte.
          </p>
          <img src="https://cdn.magicpatterns.com/uploads/qUb4Yp8e1qcjSx6WBDA6Mc/VISA_1.4.png" alt="Visa-Prep" className="mx-auto w-full max-w-sm rounded-xl shadow-2xl border border-slate-700 mb-10" data-id="element-68" data-mp="3mwzinp75o15u" />
        </div>
      </section>

      {/* WHAT YOU GET */}
      <section className="py-20 px-4 bg-slate-50" data-id="element-69" data-mp="387tjg18biaxy">
        <div className="max-w-5xl mx-auto" data-id="element-70" data-mp="347jfsv7zzwm9">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-16" data-id="element-71" data-mp="348ktz8kb8z87">
            Todo lo que incluye tu acceso hoy
          </h2>

          <div className="grid md:grid-cols-2 gap-8" data-id="element-72" data-mp="2u9vfzqvjbyx8">
            {[{
            icon: <FileText className="text-blue-600" size={32} data-id="element-73" data-mp="2pbbwry8bt18k" />,
            title: 'La Guía Maestra Visa-Prep (PDF)',
            desc: 'Instrucciones paso a paso para los formularios I-485, I-130, I-864 e I-131.'
          }, {
            icon: <FolderOpen className="text-yellow-600" size={32} data-id="element-74" data-mp="2knu7wbtfc4ll" />,
            title: 'El Método de Etiquetado Color-Code',
            desc: 'Cómo organizar físicamente tu carpeta para que el oficial la ame.'
          }, {
            icon: <LayoutList className="text-green-600" size={32} data-id="element-75" data-mp="2yez2gzhxq6p2" />,
            title: 'Matriz de Evidencia Cruzada',
            desc: 'La lista exacta de documentos que prueban que tu relación es real.'
          }, {
            icon: <ListChecks className="text-purple-600" size={32} data-id="element-76" data-mp="2pjt3okh30ow1" />,
            title: "Checklist de 'Cero Errores'",
            desc: 'Una lista de verificación final para antes de cerrar el sobre.'
          }, {
            icon: <MessageSquare className="text-orange-600" size={32} data-id="element-77" data-mp="3m4jx4fngase6" />,
            title: 'Guía de Preparación para la Entrevista',
            desc: 'Las preguntas psicológicas que realmente importan.'
          }].map((item, i) => <div key={i} className="bg-white p-8 rounded-2xl shadow-sm border border-slate-200 flex gap-6 hover:shadow-md transition-shadow" data-id="element-78" data-mp="1kecoxf4g6ttk" data-mpi={i}>
                <div className="shrink-0" data-id="element-79" data-mp="rpp0xbsbdqcb">
                  <div className="w-16 h-16 rounded-2xl bg-slate-50 flex items-center justify-center border border-slate-100" data-id="element-80" data-mp="rqqatcxlg6sg">
                    {item.icon}
                  </div>
                </div>
                <div data-id="element-81" data-mp="11omly4qj71qk">
                  <h3 className="text-xl font-bold mb-2" data-id="element-82" data-mp="11p66lt7kg48a">{item.title}</h3>
                  <p className="text-slate-600" data-id="element-83" data-mp="wnqmq4h4k3rh">{item.desc}</p>
                </div>
              </div>)}
          </div>
        </div>
      </section>

      {/* WHO IS THIS FOR / NOT FOR */}
      <section className="py-20 px-4 bg-white" data-id="element-84" data-mp="2835emtuh85fq">
        <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-12" data-id="element-85" data-mp="hmterdq1h4bn">
          {/* FOR */}
          <div className="bg-green-50 p-8 rounded-3xl border border-green-100" data-id="element-86" data-mp="hp66ujpigv2f">
            <h3 className="text-2xl font-bold text-green-900 mb-6 flex items-center gap-3" data-id="element-87" data-mp="mm69ekn5jfos">
              <CheckCircle2 className="text-green-600" data-id="element-88" data-mp="qm6p08lzlwxf" /> Esto SÍ es para ti si:
            </h3>
            <ul className="space-y-4" data-id="element-89" data-mp="vo5r7f5zqnss">
              {['Hombres y mujeres latinos en USA tramitando su ajuste de estatus por matrimonio con ciudadano o residente.', 'Parejas que quieren hacer las cosas bien pero no tienen miles de dólares para un abogado privado.', 'Personas detallistas que necesitan la paz mental de saber que su expediente está perfecto.'].map((text, i) => <li key={i} className="flex items-start gap-3" data-id="element-90" data-mp="1jcfbudrgx5ic" data-mpi={i}>
                  <div className="w-2 h-2 rounded-full bg-green-500 mt-2 shrink-0" data-id="element-91" data-mp="1ja2kxb3qfkt5"></div>
                  <span className="text-green-900" data-id="element-92" data-mp="1objkn3ahd185">{text}</span>
                </li>)}
            </ul>
          </div>

          {/* NOT FOR */}
          <div className="bg-red-50 p-8 rounded-3xl border border-red-100" data-id="element-93" data-mp="1tfuditsmxk07">
            <h3 className="text-2xl font-bold text-red-900 mb-6 flex items-center gap-3" data-id="element-94" data-mp="1ogr7p74tz9r6">
              <XCircle className="text-red-600" data-id="element-95" data-mp="1kfffctukoafa" /> Esto NO es para ti si:
            </h3>
            <ul className="space-y-4" data-id="element-96" data-mp="1khs5huahhr3f">
              {['Personas con antecedentes penales graves o condenas por delitos violentos.', 'Casos complejos de asilo o defensa de deportación en corte.', 'Personas que buscan asesoría legal personalizada (esta es una guía educativa).'].map((text, i) => <li key={i} className="flex items-start gap-3" data-id="element-97" data-mp="23di6pfalejg9" data-mpi={i}>
                  <div className="w-2 h-2 rounded-full bg-red-500 mt-2 shrink-0" data-id="element-98" data-mp="2zwpo5w2c8y2f"></div>
                  <span className="text-red-900" data-id="element-99" data-mp="2v5pr8qt7yfzx">{text}</span>
                </li>)}
            </ul>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-20 px-4 bg-slate-900 text-white" data-id="element-100" data-mp="ta9w54u70i4b">
        <div className="max-w-5xl mx-auto" data-id="element-101" data-mp="y6h1owiofrlq">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-16" data-id="element-102" data-mp="ji4640ni4arr">
            Cómo funciona
          </h2>

          <div className="grid md:grid-cols-3 gap-8 relative" data-id="element-103" data-mp="ogp742akuyh9">
            {/* Connecting line for desktop */}
            <div className="hidden md:block absolute top-12 left-1/6 right-1/6 h-0.5 bg-slate-700 z-0" data-id="element-104" data-mp="agecco65czqt"></div>

            {[{
            icon: <Download size={32} data-id="element-105" data-mp="fe63vh2vqjuh" />,
            title: '1. Descarga',
            desc: 'Obtén acceso inmediato a la guía y los checklists en tu correo.'
          }, {
            icon: <FolderOpen size={32} data-id="element-106" data-mp="5k5zjhff9imt" />,
            title: '2. Organiza',
            desc: 'Sigue el Mapa de Evidencia para reunir tus documentos sin estrés.'
          }, {
            icon: <Send size={32} data-id="element-107" data-mp="3keekws0t74aw" />,
            title: '3. Envía',
            desc: 'Usa la lista de revisión final para sellar tu sobre con total confianza.'
          }].map((step, i) => <div key={i} className="relative z-10 text-center" data-id="element-108" data-mp="3u4q0xcxrp3y5" data-mpi={i}>
                <div className="w-24 h-24 mx-auto bg-slate-800 rounded-full flex items-center justify-center border-4 border-slate-900 shadow-[0_0_0_2px_rgba(51,65,85,1)] mb-6 text-yellow-400" data-id="element-109" data-mp="3u613j9fh85no">
                  {step.icon}
                </div>
                <h3 className="text-2xl font-bold mb-3" data-id="element-110" data-mp="2wvb11t1cjtf">{step.title}</h3>
                <p className="text-slate-400" data-id="element-111" data-mp="gxft1vr4mblv">{step.desc}</p>
              </div>)}
          </div>
        </div>
      </section>

      {/* PROOF / BELIEVABILITY */}
      <section className="py-20 px-4 bg-yellow-500 text-slate-900" data-id="element-112" data-mp="bws55iy72avv">
        <div className="max-w-4xl mx-auto text-center" data-id="element-113" data-mp="2ipkubxuiml99">
          <h2 className="text-3xl md:text-4xl font-bold mb-8" data-id="element-114" data-mp="2dq7zapthq7vl">
            Qué cambia en 7 días:
          </h2>
          <p className="text-xl md:text-2xl font-medium leading-relaxed" data-id="element-115" data-mp="1rxkdhil2t678">
            En lugar de mirar una pila de papeles con confusión, tendrás un plan
            de acción claro. Sabrás exactamente qué documento va detrás de qué
            formulario y por qué. La ansiedad desaparece cuando tienes un
            sistema que seguir.
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 px-4 bg-slate-50" data-id="element-116" data-mp="18ypyq9lm45r2">
        <div className="max-w-3xl mx-auto" data-id="element-117" data-mp="190142duphlka">
          <h2 className="text-3xl font-bold text-center mb-12" data-id="element-118" data-mp="1dw7yb9dqp8me">
            Preguntas Frecuentes
          </h2>
          <div className="space-y-4" data-id="element-119" data-mp="1iv2paylv23hl">
            {[{
            q: '¿Necesito ser experto en leyes?',
            a: 'No, la guía usa lenguaje claro y directo.'
          }, {
            q: '¿Sirve para cualquier estado de USA?',
            a: 'Sí, los procesos de USCIS son federales y aplican igual en todo el país.'
          }, {
            q: '¿Esto garantiza mi residencia?',
            a: 'Nadie puede garantizar eso, pero esta guía reduce drásticamente las probabilidades de rechazo por errores humanos.'
          }, {
            q: '¿Es seguro el pago?',
            a: 'Sí, usamos procesadores encriptados de alta seguridad.'
          }].map((faq, i) => <details key={i} className="group bg-white rounded-xl border border-slate-200 overflow-hidden" data-id="element-120" data-mp="402qik9s28io" data-mpi={i}>
                <summary className="flex items-center justify-between p-6 font-bold cursor-pointer list-none text-lg" data-id="element-121" data-mp="3r3ps2zbyrepp">
                  {faq.q}
                  <ChevronDown className="transition-transform group-open:rotate-180 text-slate-400" data-id="element-122" data-mp="3m5xxtynujgic" />
                </summary>
                <div className="px-6 pb-6 text-slate-600" data-id="element-123" data-mp="1pa9xxsgo3hpz">{faq.a}</div>
              </details>)}
          </div>
        </div>
      </section>

      {/* OFFER + PRICE BLOCK + CTA */}
      <section id="checkout" className="py-24 px-4 bg-slate-900 text-white relative" data-id="element-124" data-mp="2c6km53rosckp">
        <div className="max-w-3xl mx-auto text-center" data-id="element-125" data-mp="27acba2kiv5jc">
          <h2 className="text-3xl md:text-4xl font-bold mb-6" data-id="element-126" data-mp="22ghm4jm5dycv">
            Toma el control de tu futuro hoy mismo
          </h2>
          <p className="text-xl text-slate-300 mb-12" data-id="element-127" data-mp="1xi6ca8vurnun">
            Consigue hoy el Sistema Visa-Prep: La Guía Universal de Inmigración
            por una fracción de lo que costaría una sola hora de consulta con un
            abogado.
          </p>

          <div className="bg-slate-800 p-8 md:p-12 rounded-3xl border border-slate-700 shadow-2xl relative overflow-hidden" data-id="element-128" data-mp="1ji5amr7rkce4">
            <div className="absolute top-0 right-0 bg-yellow-500 text-slate-900 font-bold px-4 py-1 rounded-bl-xl text-sm" data-id="element-129" data-mp="1ekvn5h3xqasx">
              OFERTA ESPECIAL
            </div>

            <div className="mb-10" data-id="element-130" data-mp="38x3ao9dxfpeo">
              <CountdownTimer durationHours={24} variant="light" data-id="element-131" data-mp="38y4oumq8os0m" />
            </div>

            <div className="mb-8" data-id="element-132" data-mp="3hdu8prqo6xwr">
              <span className="text-slate-400 line-through text-xl block mb-2" data-id="element-133" data-mp="3hcj3dnhkti3j">
                Valor Real: $57.00 USD
              </span>
              <div className="flex items-center justify-center gap-2" data-id="element-134" data-mp="3q6nrhz5bsffd">
                <span className="text-2xl font-bold text-yellow-400" data-id="element-135" data-mp="3v60ohcpwv2vo">$</span>
                <span className="text-6xl font-extrabold text-white" data-id="element-136" data-mp="3v84qtq85vttu">27</span>
                <span className="text-2xl font-bold text-yellow-400" data-id="element-137" data-mp="2yhc96o5kxtpy">.00</span>
                <span className="text-slate-400 ml-2" data-id="element-138" data-mp="32iearbmyyhv3">USD</span>
              </div>
            </div>

            <a href="https://tuvidadigitalmejora.com/cart/51890895323457:1" onClick={handleCheckoutClick} className="w-full bg-yellow-500 hover:bg-yellow-400 text-slate-900 font-extrabold text-xl py-5 px-8 rounded-2xl shadow-[0_0_30px_rgba(234,179,8,0.3)] transition-all transform hover:scale-[1.02] flex flex-col items-center justify-center gap-1" data-id="element-139" data-mp="37fegflfwpilo">
              <span data-id="element-140" data-mp="204lw98emy34">QUIERO MI GUÍA VISA-PREP AHORA</span>
              <span className="text-sm font-medium opacity-80" data-id="element-141" data-mp="3t7h7r1k8fzpd">
                Acceso inmediato tras la compra. Tu futuro no puede esperar.
              </span>
            </a>

            <div className="mt-6 flex items-center justify-center gap-4 text-slate-400 text-sm" data-id="element-142" data-mp="bx9byk5dfdp7">
              <span className="flex items-center gap-1" data-id="element-143" data-mp="6y66iylhb5gp">
                <Lock size={16} data-id="element-144" data-mp="3fh3z0wziogyz" /> Pago Seguro
              </span>
              <span className="flex items-center gap-1" data-id="element-145" data-mp="3ah7veyuqerjy">
                <Download size={16} data-id="element-146" data-mp="3aisqqqoveeiv" /> Descarga Inmediata
              </span>
            </div>
          </div>
        </div>
      </section>

      <LegalFooter data-id="element-147" data-mp="3kf5g5cddu67t" />
    </div>;
}